/**
 * Capital Accounts Tab Component
 *
 * Displays capital account data for all investors in a fund.
 * Shows: Investor Name, Committed Capital, Drawn Capital, Undrawn Capital,
 *        Invested Capital, Fees & Costs, Net Asset Value
 * Includes a Total Fund row summing all values.
 */

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatCurrency } from '@/utils/formatters';
import { useFundCapitalAccounts } from '@/hooks/react-query/useCapitalAccounts';
import { RefreshCw, Users } from 'lucide-react';

interface CapitalAccountsTabProps {
  fundId: string;
}

/**
 * Format value with negative display: red color and parentheses
 * Fees are always displayed as negative (costs)
 */
function formatFeesDisplay(value: string): string {
  const numValue = Number.parseFloat(value);
  const formatted = formatCurrency(Math.abs(numValue));
  // Fees are always shown as negative (in parentheses)
  return numValue === 0 ? `-${formatted}` : `(${formatted})`;
}

export function CapitalAccountsTab({ fundId }: CapitalAccountsTabProps) {
  const capitalAccountsQuery = useFundCapitalAccounts(fundId);
  const isLoading = capitalAccountsQuery.isLoading;
  const capitalAccounts = capitalAccountsQuery.data;

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-8">
        <RefreshCw className="h-6 w-6 animate-spin mr-2" />
        Loading capital accounts...
      </div>
    );
  }

  if (!capitalAccounts || capitalAccounts.investors.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <Users className="h-12 w-12 mx-auto mb-4 text-muted-foreground/60" />
        No capital accounts data available
      </div>
    );
  }

  return (
    <div className="border rounded-lg bg-card">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="uppercase text-xs font-semibold text-muted-foreground">
              Investor Name
            </TableHead>
            <TableHead className="text-right uppercase text-xs font-semibold text-muted-foreground">
              Committed Capital
            </TableHead>
            <TableHead className="text-right uppercase text-xs font-semibold text-muted-foreground">
              Drawn Capital
            </TableHead>
            <TableHead className="text-right uppercase text-xs font-semibold text-muted-foreground">
              Undrawn Capital
            </TableHead>
            <TableHead className="text-right uppercase text-xs font-semibold text-muted-foreground">
              Invested Capital
            </TableHead>
            <TableHead className="text-right uppercase text-xs font-semibold text-muted-foreground">
              Fees & Costs
            </TableHead>
            <TableHead className="text-right uppercase text-xs font-semibold text-muted-foreground">
              Net Asset Value
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {/* Investor rows */}
          {capitalAccounts.investors.map((investor) => (
            <TableRow key={investor.investor_id}>
              <TableCell className="font-medium flex items-center gap-2">
                {investor.investor_name}
              </TableCell>
              <TableCell className="text-right">
                {formatCurrency(investor.committed_capital)}
              </TableCell>
              <TableCell className="text-right">
                {formatCurrency(investor.drawn_capital)}
              </TableCell>
              <TableCell className="text-right">
                {formatCurrency(investor.undrawn_capital)}
              </TableCell>
              <TableCell className="text-right">
                {formatCurrency(investor.invested_capital)}
              </TableCell>
              <TableCell className="text-right text-red-500">
                {formatFeesDisplay(investor.fees_and_costs)}
              </TableCell>
              <TableCell className="text-right">
                {formatCurrency(investor.nav)}
              </TableCell>
            </TableRow>
          ))}

          {/* Total Fund row */}
          <TableRow className="bg-muted/50 font-semibold border-t-2">
            <TableCell className="font-bold">TOTAL FUND</TableCell>
            <TableCell className="text-right font-bold">
              {formatCurrency(capitalAccounts.total_committed)}
            </TableCell>
            <TableCell className="text-right font-bold">
              {formatCurrency(capitalAccounts.total_drawn)}
            </TableCell>
            <TableCell className="text-right font-bold">
              {formatCurrency(capitalAccounts.total_undrawn)}
            </TableCell>
            <TableCell className="text-right font-bold">
              {formatCurrency(capitalAccounts.total_invested)}
            </TableCell>
            <TableCell className="text-right font-bold text-red-500">
              {formatFeesDisplay(capitalAccounts.total_fees)}
            </TableCell>
            <TableCell className="text-right font-bold">
              {formatCurrency(capitalAccounts.total_nav)}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </div>
  );
}
